/**
 * Calmy product gallery.
 *
 * One list of the product's real media, read two ways: a two-column grid on
 * desktop, a scroll-snap track with dots on mobile. Swiping is the browser's
 * own overflow scrolling, so it is smooth, interruptible and works with a
 * trackpad and a keyboard as well as a thumb — there is no touch handler here
 * to get wrong.
 *
 * This file also owns:
 *   · the dots, driven by which slide is actually centred
 *   · __goToMedia(id), which the variant picker in calmy-pdp.js calls when the
 *     selected variant carries its own image
 *   · the viewer: close, previous, next, Escape, arrow keys, a focus trap and
 *     focus returned to the thumbnail that opened it
 *
 * NO DUPLICATE HANDLERS: each gallery carries its own flag, so a theme-editor
 * section reload re-running boot() over a wired gallery is a no-op.
 */
(function () {
  "use strict";

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");
  var FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

  /* ---------------- The viewer ---------------- */

  var viewer = null;

  function buildViewer() {
    if (viewer) return viewer;
    var el = document.createElement("div");
    el.className = "cgv";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.setAttribute("aria-label", "Product image");
    el.innerHTML =
      '<button type="button" class="cgv__btn cgv__close" data-cgv-close aria-label="Close">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>' +
      '<button type="button" class="cgv__btn cgv__prev" data-cgv-prev aria-label="Previous image">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="cgv__btn cgv__next" data-cgv-next aria-label="Next image">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<figure class="cgv__fig"><img class="cgv__img" alt=""><figcaption class="cgv__cap" data-cgv-cap></figcaption></figure>';
    document.body.appendChild(el);
    viewer = el;
    return el;
  }

  var state = { items: [], i: 0, opener: null, lockedY: 0 };

  function render() {
    var el = buildViewer();
    var it = state.items[state.i];
    if (!it) return;
    var img = el.querySelector(".cgv__img");
    img.src = it.full;
    img.alt = it.alt || "";
    el.querySelector("[data-cgv-cap]").textContent =
      state.items.length > 1 ? state.i + 1 + " / " + state.items.length : "";
    /* One image means no way to go anywhere; the controls go rather than sit
       there disabled. */
    el.querySelector("[data-cgv-prev]").hidden = state.items.length < 2;
    el.querySelector("[data-cgv-next]").hidden = state.items.length < 2;
  }

  function step(n) {
    if (!state.items.length) return;
    state.i = (state.i + n + state.items.length) % state.items.length;
    render();
  }

  function trap(e) {
    if (e.key !== "Tab" || !viewer || !viewer.classList.contains("is-on")) return;
    var f = Array.prototype.filter.call(viewer.querySelectorAll(FOCUSABLE), function (n) {
      return !n.hidden && n.offsetParent !== null;
    });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function onKey(e) {
    if (!viewer || !viewer.classList.contains("is-on")) return;
    if (e.key === "Escape") { e.preventDefault(); closeViewer(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    else if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    else trap(e);
  }

  function openViewer(items, index, opener) {
    state.items = items;
    state.i = index;
    state.opener = opener || null;
    var el = buildViewer();
    render();
    /* The same scroll lock the drawers use: position:fixed on the body, because
       overflow:hidden does not hold iOS Safari. */
    state.lockedY = window.scrollY || 0;
    document.body.style.top = -state.lockedY + "px";
    document.body.classList.add("calmy-lock");
    el.classList.add("is-on");
    document.addEventListener("keydown", onKey, true);
    window.setTimeout(function () {
      var c = el.querySelector("[data-cgv-close]");
      if (c) { try { c.focus({ preventScroll: true }); } catch (err) { c.focus(); } }
    }, 60);
  }

  function closeViewer() {
    if (!viewer) return;
    viewer.classList.remove("is-on");
    document.removeEventListener("keydown", onKey, true);
    document.body.classList.remove("calmy-lock");
    document.body.style.top = "";
    window.scrollTo(0, state.lockedY);
    /* Back to the thumbnail that opened it, which is where the reader was. */
    if (state.opener) {
      try { state.opener.focus({ preventScroll: true }); } catch (e) { state.opener.focus(); }
      state.opener = null;
    }
  }

  document.addEventListener("click", function (e) {
    if (!viewer) return;
    var t = e.target;
    if (t.closest && t.closest("[data-cgv-close]")) { closeViewer(); return; }
    if (t.closest && t.closest("[data-cgv-prev]")) { step(-1); return; }
    if (t.closest && t.closest("[data-cgv-next]")) { step(1); return; }
    /* The backdrop is the dialog itself; a click on the picture is not a
       click on the backdrop. */
    if (t === viewer) closeViewer();
  });

  /* ---------------- A gallery ---------------- */

  function init(root) {
    if (root.__calmyGal) return;
    root.__calmyGal = true;

    var track = root.querySelector("[data-cgal-track]");
    if (!track) return;
    var items = Array.prototype.slice.call(root.querySelectorAll("[data-cgal-item]"));
    var dots = root.querySelector("[data-cgal-dots]");

    /* The list the viewer shows: images only, at full size, in saved order. */
    function collect() {
      return items.map(function (li) {
        var img = li.querySelector("img");
        if (!img) return null;
        return {
          full: (img.currentSrc || img.src || "").replace(/(\?|&)width=\d+/, "$1width=1800"),
          alt: img.getAttribute("alt") || ""
        };
      }).filter(Boolean);
    }

    /* Open from any tile. The index passed is the tile's, but the viewer's list
       skips videos, so it is mapped rather than assumed. */
    root.addEventListener("click", function (e) {
      var btn = e.target.closest && e.target.closest("[data-cgal-open]");
      if (!btn) return;
      e.preventDefault();
      var li = btn.closest("[data-cgal-item]");
      var imgItems = items.filter(function (x) { return x.querySelector("img"); });
      var idx = Math.max(0, imgItems.indexOf(li));
      openViewer(collect(), idx, btn);
    });

    /* ---- dots ---- */
    if (dots && items.length > 1) {
      items.forEach(function () {
        var d = document.createElement("span");
        d.className = "cpgal__dot";
        dots.appendChild(d);
      });
      var marks = Array.prototype.slice.call(dots.children);
      var paint = function () {
        /* Which slide is nearest the track's centre — correct mid-swipe, not
           only when a snap settles. */
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0, bestD = Infinity;
        items.forEach(function (li, i) {
          var c = li.offsetLeft + li.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < bestD) { bestD = d; best = i; }
        });
        marks.forEach(function (m, i) { m.classList.toggle("is-on", i === best); });
      };
      track.addEventListener("scroll", function () {
        window.requestAnimationFrame(paint);
      }, { passive: true });
      paint();
      root.__paintDots = paint;
    }

    /* ---- variant image ---- */
    root.__goToMedia = function (mediaId) {
      if (!mediaId) return;
      var li = items.filter(function (x) { return x.getAttribute("data-media-id") === String(mediaId); })[0];
      if (!li) return;
      if (window.matchMedia("(min-width: 1000px)").matches) {
        /* On the grid there is nothing to scroll to horizontally; the tile is
           marked instead so the selected finish is identifiable, and only
           brought into view if it is off screen. */
        items.forEach(function (x) { x.classList.toggle("is-active", x === li); });
        var r = li.getBoundingClientRect();
        if (r.top < 0 || r.bottom > window.innerHeight) {
          li.scrollIntoView({ block: "nearest", behavior: REDUCED.matches ? "auto" : "smooth" });
        }
      } else {
        track.scrollTo({ left: li.offsetLeft - track.offsetLeft, behavior: REDUCED.matches ? "auto" : "smooth" });
        if (root.__paintDots) window.setTimeout(root.__paintDots, 260);
      }
    };
  }

  function boot(scope) {
    (scope || document).querySelectorAll("[data-cgal]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
